/**
 * The qFlow workspace whose runs are read. qFlow is one connection; the
 * workspace is picked here, per live dataset, and saved as its filters'
 * `environments` (exactly one). The list comes from the source's filter
 * options, each workspace with its name; with none picked, the backend lists
 * only the workspaces and reads no runs.
 */

import { FilterButton } from "./filter-button";
import { useTraceFilters } from "./hooks";
import { NEW_QUERY, type TraceQuery } from "./trace-query";

/** The workspace picked, or null. */
export const qflowWorkspace = (q: TraceQuery): string | null => q.environments[0] ?? null;

/** Every workspace's name by id, from the cheap listing (no workspace picked). */
export function useWorkspaceNames(sourceId: string): Record<string, string> {
  const listed = useTraceFilters(sourceId, NEW_QUERY);
  return Object.fromEntries((listed.data?.environments ?? []).map((w) => [w.value, w.label ?? w.value]));
}

export function WorkspacePicker({
  sourceId,
  value,
  onChange,
}: {
  sourceId: string;
  value: TraceQuery;
  onChange: (next: TraceQuery) => void;
}) {
  const names = useWorkspaceNames(sourceId);
  const picked = qflowWorkspace(value);
  const choices = Object.entries(names).map(([id, name]) => ({ value: id, label: name }));
  if (picked && !names[picked]) choices.push({ value: picked, label: picked });
  return (
    <FilterButton
      label="Workspace"
      single
      search
      choices={choices}
      chosen={picked ? [picked] : []}
      // Workflow ids belong to one workspace: another workspace starts with none picked.
      onChange={(ids) =>
        onChange({
          ...value,
          environments: ids.slice(0, 1),
          traceNames: ids[0] === picked ? value.traceNames : [],
        })
      }
    />
  );
}
